/**
 * @file TranscriptEditSheet.tsx
 * @brief Edit a transcript selection on the web: correct the words, or say
 * who actually said them.
 *
 * @details
 * Web parity with the app's press-and-hold "Correct" and "Who said this?"
 * (DES, 29-09-2026). The owner selects text in a transcript line; the
 * transcript tab offers both actions and opens this sheet.
 *
 * - **Correct**: replace the selection (empty = it was never said), in this
 *   line only or everywhere it appears. The server carries the change into
 *   the intel by context (meetingCorrections.service.mjs).
 * - **Who said this?**: this meeting's other voices, then invitees with no
 *   voice yet, then anyone by name/email. The server snaps the selection to
 *   whole sentences and splits the line (meetingSpeakerMove.service.mjs).
 *
 * Both routes are owner-only, so the tab only offers this to the owner.
 *
 * @references
 * - FRONTEND/.../MeetingDetails/components/WhoSaidThisPicker.tsx (the app twin)
 * - ../api.ts (useCorrectMeetingTranscript, useMoveTranscriptSpeaker)
 *
 * @author DES
 * @date 29-09-2026
 */

import { useEffect, useMemo, useState } from "react";
import { LoaderCircle } from "lucide-react";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { ApiClientError } from "@/shared/api/client";
import { useAuth } from "@/shared/auth/auth-context";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import {
  useCorrectMeetingTranscript,
  useMoveTranscriptSpeaker,
  type SpeakerMoveTarget,
} from "../api";
import type { MeetingDetail } from "../schemas";

export type TranscriptSelection = {
  segmentId: string;
  /** Who the line is credited to now. */
  speakerId: string;
  text: string;
  /** Offset of the selection in the line's stored text. */
  start: number;
};

export type TranscriptEditMode = "correct" | "speaker";

type Person = {
  key: string;
  name: string;
  subtitle?: string;
  avatar?: string | null;
  target: SpeakerMoveTarget;
};

const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  return parts.length === 0
    ? "?"
    : parts
        .map((p) => p[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof ApiClientError ? err.message : fallback;
}

export function TranscriptEditSheet({
  open,
  onOpenChange,
  mode,
  meeting,
  selection,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: TranscriptEditMode;
  meeting: MeetingDetail;
  selection: TranscriptSelection | null;
  /** After a successful save (clear the page's selection). */
  onDone: () => void;
}) {
  const correct = useCorrectMeetingTranscript();
  const move = useMoveTranscriptSpeaker();
  const pending = correct.isPending || move.isPending;

  const [replacement, setReplacement] = useState("");
  const [everywhere, setEverywhere] = useState(false);
  const [pickedKey, setPickedKey] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");

  useEffect(() => {
    if (!open) return;
    setReplacement(selection?.text ?? "");
    setEverywhere(false);
    setPickedKey(null);
    setNewName("");
    setNewEmail("");
  }, [open, selection]);

  // "This is me" (DES 10-10-2026): the signed-in owner as their real account,
  // for a quick meeting whose roster does not hold them. Never a guest.
  const { user } = useAuth();
  const me = useMemo<Person | null>(() => {
    const email = (user?.email ?? "").trim();
    if (!user?.id || !email) return null;
    const name =
      [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.name || email;
    return {
      key: "me",
      name: "This is me",
      subtitle: email,
      avatar: user.profileImageUrl ?? null,
      target: {
        name,
        email,
        isExternal: false,
        profileImageUrl: user.profileImageUrl ?? null,
        isRegisteredUser: true,
      },
    };
  }, [user]);

  // This meeting's voices (minus the line's own), then invitees with no voice.
  const people = useMemo<Person[]>(() => {
    const out: Person[] = [];
    const speakers = meeting.resolved_speakers ?? [];
    const seenEmail = new Set<string>();
    const seenName = new Set<string>();
    for (const s of speakers) {
      const id = s.speakerId ?? s.speaker_id ?? "";
      const row = s as { email?: string | null; resolvedUser?: { email?: string | null } | null };
      const email = norm(row.email ?? row.resolvedUser?.email);
      const name = (s.name ?? s.resolvedUser?.name ?? "").trim();
      if (email) seenEmail.add(email);
      if (name) seenName.add(norm(name));
      if (!id || id === selection?.speakerId || !name) continue;
      out.push({
        key: `spk:${id}`,
        name,
        subtitle: (s.isExternal ?? s.is_external) ? "External · in this meeting" : "In this meeting",
        avatar: s.profileImageUrl ?? s.profile_image_url ?? s.resolvedUser?.profileImageUrl ?? null,
        target: { speakerId: id },
      });
    }
    for (const p of meeting.attendees ?? meeting.participants ?? []) {
      const name = (p.name ?? "").trim();
      const email = norm(p.email);
      if (!name) continue;
      if (email ? seenEmail.has(email) : seenName.has(norm(name))) continue;
      out.push({
        key: `p:${p.id ?? email ?? name}`,
        name,
        subtitle: p.email || "Invited, no voice yet",
        avatar: p.profileImageUrl ?? null,
        target: {
          name,
          email: p.email || undefined,
          isExternal: !!p.isExternal,
          profileImageUrl: p.profileImageUrl ?? null,
          isRegisteredUser: !!p.isRegisteredUser,
        },
      });
    }
    // "This is me" on top, only when the owner is neither a voice nor an
    // invitee here (no duplicate; a roster with them keeps today's rows).
    const meEmail = norm(me?.subtitle);
    const listed =
      seenEmail.has(meEmail) ||
      (meeting.attendees ?? meeting.participants ?? []).some(
        (p) => norm(p.email) === meEmail || (!!user?.id && p.id === user.id),
      );
    if (me && meEmail && !listed) out.unshift(me);
    return out;
  }, [meeting.resolved_speakers, meeting.attendees, meeting.participants, selection?.speakerId, me, user?.id]);

  const picked = people.find((p) => p.key === pickedKey) ?? null;
  const someoneElse = pickedKey === "new";
  const canSave =
    !!selection &&
    !pending &&
    (mode === "correct"
      ? replacement.trim() !== selection.text.trim()
      : !!picked || (someoneElse && newName.trim().length > 0));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selection || !canSave) return;
    try {
      if (mode === "correct") {
        const res = await correct.mutateAsync({
          meetingId: meeting.id,
          segmentId: selection.segmentId,
          from: selection.text,
          to: replacement.trim(),
          scope: everywhere ? "all" : "one",
          offset: selection.start,
        });
        toast({
          variant: "success",
          title: replacement.trim() ? "Transcript corrected" : "Words removed",
          description: res.message,
        });
      } else {
        // Your own email in "someone else" is you, your account. (DES 10-10-2026)
        const isMe = !!me && !!newEmail.trim() && norm(newEmail) === norm(me.subtitle);
        const to: SpeakerMoveTarget = picked
          ? picked.target
          : isMe && me
            ? me.target
            : { name: newName.trim(), email: newEmail.trim() || undefined, isExternal: true };
        const res = await move.mutateAsync({
          meetingId: meeting.id,
          segmentId: selection.segmentId,
          selectedText: selection.text,
          start: selection.start,
          to,
        });
        toast({
          variant: "success",
          title: "Speaker changed",
          description:
            res.message ??
            `Now credited to ${
              picked
                ? picked.key === "me" && "name" in picked.target
                  ? picked.target.name
                  : picked.name
                : newName.trim()
            }.`,
        });
      }
      onDone();
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: mode === "correct" ? "Could not correct" : "Could not change the speaker",
        description: errorText(err, "Try again."),
      });
    }
  };

  const quote =
    (selection?.text.length ?? 0) > 140
      ? `${selection?.text.slice(0, 139)}…`
      : selection?.text ?? "";

  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      {/* DES 01-10-2026: form standard. Cmd+K palette frame, capped at
          max-w-3xl (a short editor); the editing layout itself is unchanged. */}
      <FormSheetContent side="palette" className="max-w-3xl">
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col overflow-hidden" noValidate>
          <FormSheetFrame>
            <FormSheetHeader>
              <FormSheetTitle>{mode === "correct" ? "Correct the transcript" : "Who said this?"}</FormSheetTitle>
              <FormSheetDescription>
                {mode === "correct"
                  ? "Fix what speech recognition misheard. The summary, decisions and to-dos follow the correction."
                  : "Credit these words to the person who said them. The line is split at whole sentences."}
              </FormSheetDescription>
            </FormSheetHeader>

            <FormSheetBody>
              <FormSheetSection title="Selected">
                {/* In a padded cell, so the quote sits on the fields' side
                    padding instead of the frame edge (DES 01-10-2026: form
                    standard). */}
                <FormSheetFields className="sm:grid-cols-1">
                  <FormSheetField span={2}>
                    <p
                      dir="auto"
                      className="rounded-[1.2rem] border border-border bg-muted/40 px-5 py-3 text-[15px] leading-relaxed text-foreground/90"
                    >
                      “{quote}”
                    </p>
                  </FormSheetField>
                </FormSheetFields>
              </FormSheetSection>

              {mode === "correct" ? (
                <FormSheetSection title="Correction" subtitle="Leave it empty if these words were never said.">
                  <FormSheetFields>
                    <FormSheetField span={2}>
                      <FormFieldGroup label="Replace with" htmlFor="transcript-fix">
                        <Input
                          id="transcript-fix"
                          dir="auto"
                          autoFocus
                          value={replacement}
                          maxLength={120}
                          onChange={(e) => setReplacement(e.target.value)}
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField span={2}>
                      <label className="flex cursor-pointer items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={everywhere}
                          onChange={(e) => setEverywhere(e.target.checked)}
                          className="h-4 w-4 accent-[hsl(var(--brand))]"
                        />
                        Everywhere these words appear in this meeting
                      </label>
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>
              ) : (
                <FormSheetSection title="Said by">
                  {/* Two columns of people in the wider palette frame, on the
                      fields' side padding (DES 01-10-2026: form standard). */}
                  <ul className="grid grid-cols-1 gap-1 px-4 py-3 sm:grid-cols-2 md:px-5">
                    {people.map((p) => {
                      const active = pickedKey === p.key;
                      return (
                        <li key={p.key}>
                          <button
                            type="button"
                            onClick={() => setPickedKey(p.key)}
                            aria-pressed={active}
                            className={cn(
                              "flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left transition-colors",
                              active ? "bg-brand-soft" : "hover:bg-accent",
                            )}
                          >
                            <Avatar src={p.avatar} initials={initials(p.name)} alt="" size="sm" tone="brand" />
                            <span className="min-w-0 flex-1">
                              <span className={cn("block truncate text-sm font-semibold", active && "text-brand")}>
                                {p.name}
                              </span>
                              {p.subtitle ? (
                                <span className="block truncate text-xs text-muted-foreground">{p.subtitle}</span>
                              ) : null}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                    <li>
                      <button
                        type="button"
                        onClick={() => setPickedKey("new")}
                        aria-pressed={someoneElse}
                        className={cn(
                          "flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left text-sm font-semibold transition-colors",
                          someoneElse ? "bg-brand-soft text-brand" : "hover:bg-accent",
                        )}
                      >
                        <Avatar initials="+" alt="" size="sm" tone="brand" />
                        Someone else
                      </button>
                    </li>
                  </ul>
                  {someoneElse ? (
                    <FormSheetFields>
                      <FormSheetField>
                        {/* Hint so its hairline lines up with Email's. */}
                        <FormFieldGroup
                          label="Name"
                          htmlFor="speaker-name"
                          required
                          hint="As it should read in the transcript."
                        >
                          <Input
                            capitalizeFirst
                            id="speaker-name"
                            autoFocus
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                          />
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField>
                        <FormFieldGroup
                          label="Email"
                          htmlFor="speaker-email"
                          hint="Optional. Links them if they have a Verblizr account."
                        >
                          <Input
                            id="speaker-email"
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                          />
                        </FormFieldGroup>
                      </FormSheetField>
                    </FormSheetFields>
                  ) : null}
                </FormSheetSection>
              )}
            </FormSheetBody>

            <FormSheetFooter>
              <FormSheetFooterCell>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  disabled={pending}
                  className={formSheetActionClass}
                >
                  Cancel
                </Button>
              </FormSheetFooterCell>
              <FormSheetFooterCell>
                <Button
                  type="submit"
                  disabled={!canSave}
                  className={cn(formSheetActionClass, "bg-primary text-primary-foreground hover:bg-primary/90")}
                >
                  {pending ? (
                    <>
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
              </FormSheetFooterCell>
            </FormSheetFooter>
          </FormSheetFrame>
        </form>
      </FormSheetContent>
    </FormSheet>
  );
}
